import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Settings2 } from 'lucide-react'
import { buildStageMap } from './useStatusStage'
import type { ReactNode } from 'react'
import type { PickerAction, PickerOption } from '@/components/app/combo-box/PickerPanel'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import { compareRanks } from '@/lib/utils/lexorank'
import { StageGlyph } from '@/components/app/indicators/StageGlyph'
import { stageForProgressType } from '@/lib/utils/status-stage'
import { isReservedStatusName } from '@/routes/features/settings/status/constants/reservedStatusNames'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'

/** Every status row carries its stage glyph. */
type StatusPickerOption = PickerOption & { icon: ReactNode }

const STAGE_ORDER = ['BL', 'PL', 'IP', 'CD', 'CL']

const stageGroupKey = (status: StatusDocument) => {
  if (isReservedStatusName(status.name)) return 'agent'
  const type = status.progressType.toUpperCase()
  if (type === 'BL' || type === 'PL') return 'notStarted'
  if (type === 'IP') return 'active'
  return 'closed'
}

/**
 * Workflow order: stage first (Backlog → Cancelled), then the workspace's own rank
 * within a stage. The AI-lane statuses leave the picker while Super Agent is off
 * (design 31a) — except the task's current one, so the trigger never hides the value
 * you're changing. With Super Agent on they sit last, in their own "Agent" group.
 */
function orderStatuses(statuses: Array<StatusDocument>, selectedId?: string | null) {
  const showAgentLane = FEATURE_FLAGS.ENABLE_SUPER_AGENT
  return statuses
    .filter((s) => showAgentLane || s.id === selectedId || !isReservedStatusName(s.name))
    .sort((a, b) => {
      const agent = Number(isReservedStatusName(a.name)) - Number(isReservedStatusName(b.name))
      if (agent !== 0) return agent
      const stage =
        STAGE_ORDER.indexOf(a.progressType.toUpperCase()) -
        STAGE_ORDER.indexOf(b.progressType.toUpperCase())
      return stage !== 0 ? stage : compareRanks(a.sortOrder, b.sortOrder)
    })
}

/**
 * The status picker's rows (design 31a): grouped by stage, one glyph per stage, and
 * "Edit workflow…" at the foot. Shared by every status picker so the sidebar chip and
 * the title ring list the same statuses with the same shapes.
 */
export function useStatusPickerOptions(
  statuses: Array<StatusDocument>,
  selectedStatusId?: string | null,
): { options: Array<StatusPickerOption>; action?: PickerAction } {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { workspaceSlug } = useParams({ strict: false })

  const options = useMemo<Array<StatusPickerOption>>(() => {
    const stages = buildStageMap(statuses)
    return orderStatuses(statuses, selectedStatusId).map((status) => {
      const stage = stages.get(status.id) ?? stageForProgressType(status.progressType)
      return {
        value: status.id,
        label: status.name,
        icon: <StageGlyph stage={stage} />,
        group: t(`picker.status.groups.${stageGroupKey(status)}`),
      }
    })
  }, [statuses, selectedStatusId, t])

  const action = workspaceSlug
    ? {
        label: t('picker.status.editWorkflow'),
        icon: <Settings2 className="size-[13px]" />,
        onSelect: () => {
          void navigate({
            to: '/$workspaceSlug/settings/statuses',
            params: { workspaceSlug },
          })
        },
      }
    : undefined

  return { options, action }
}
